import AuthCallbackPage from '@shared/components/loading/AuthCallbackPage';
import ToBeCreated from '@shared/components/loading/ToBeCreated';
import ProfileAdvancedSettingsPageContent from '@shared/components/profile/pageContent/ProfileAdvancedSettingsPageContent';
import ProfileControlBuilderPage from '@shared/components/profile/pageContent/ProfileControlBuilderPageContent';
import ProfileDictationPageContent from '@shared/components/profile/pageContent/ProfileDictationPageContent';
import ProfileHotkeysPageContent from '@shared/components/profile/pageContent/ProfileHotkeysPageContent';
import ProfileKeybindingsPageContent from '@shared/components/profile/pageContent/ProfileKeybindingsPageContent';
import ProfilePreviewPageContent from '@shared/components/profile/pageContent/ProfilePreviewPageContent';
import ProfileSharePageContent from '@shared/components/profile/pageContent/ProfileSharePageContent';
import ProfileSuggestedPromptsPageContent from '@shared/components/profile/pageContent/ProfileSuggestedPromptsPageContent';
import ProfileSummaryPageContent from '@shared/components/profile/pageContent/ProfileSummaryPageContent';
import AdminHeaderOnlyLayout from 'admin/components/layout/AdminHeaderOnlyLayout';
import ProtectedLayout from 'admin/components/ProtectedLayout';
import { useEffect } from 'react';
import { createHashRouter, RouterProvider } from 'react-router';
import LoginPage from '../LoginPage';
import NotFoundPage from '../NotFoundPage';
import AccountSettingsPage from '../protected/AccountSettingsPage';
import DashboardPage from '../protected/DashboardPage';
import MetricLandingPage from '../protected/metric/MetricLandingPage';
import NotificationPage from '../protected/NotificationPage';
import LicenseUpgradePage from '../protected/org/LicenseUpgradePage';
import OrgBillingRedirectErrorPage from '../protected/org/OrgBillingRedirectErrorPage';
import OrgBillingRedirectPage from '../protected/org/OrgBillingRedirectPage';
import OrgDetailPage from '../protected/org/OrgDetailPage';
import OrgDocumentationPage from '../protected/org/OrgDocumentationPage';
import OrgInvitesPage from '../protected/org/OrgInvitesPage';
import OrgLicenseActivatedPage from '../protected/org/OrgLicenseActivatedPage';
import OrgLicenseKeyPage from '../protected/org/OrgLicenseKeyPage';
import OrgManagedOrgsPage from '../protected/org/OrgManagedOrgsPage';
import OrgMembersPage from '../protected/org/OrgMembersPage';
import OrgProductPage from '../protected/org/OrgProductPage';
import OrgProfilesPage from '../protected/org/OrgProfilesPage';
import OrgSettingsPage from '../protected/org/OrgSettingsPage';
import OrgProfileEditPage from '../protected/org/profile/ProfileEditPage';
import ProfileLayout from '../protected/org/profile/ProfileLayout';
import ProjectClientDetailPage from '../protected/projects/ProjectClientDetailPage';
import ProjectDeviceTypeCustomControlsPage from '../protected/projects/ProjectDeviceTypeCustomControlsPage';
import ProjectDeviceTypeDetailPage from '../protected/projects/ProjectDeviceTypeDetailPage';
import ProjectsLandingPage from '../protected/projects/ProjectsLandingPage';
import ProjectsNewPage from '../protected/projects/ProjectsNewPage';
import DeveloperToolsPage from '../resources/DeveloperToolsPage';

const getBillingRoutes = () => [
    {
        path: 'upgrade',
        children: [
            {
                index: true,
                element: <OrgBillingRedirectPage />,
                handle: { crumbKey: 'common:license.redirect.title' },
            },
            {
                path: 'product/:productId',
                element: <OrgBillingRedirectPage />,
                handle: { crumbKey: 'common:license.redirect.title' },
            },
        ],
    },
    {
        path: 'error',
        element: <OrgBillingRedirectErrorPage />,
        handle: { crumbKey: 'common:license.redirect.error' },
    },
];

const getProfileRoutes = () => [
    {
        index: true,
        handle: { showActions: true },
        element: <OrgProfileEditPage />,
    },
    {
        path: 'controls/:index',
        element: <ProfileControlBuilderPage />,
        handle: { crumbKey: 'common:macros.new' },
    },
    {
        path: 'advanced',
        handle: {
            crumbKey: 'common:advancedOptions',
            title: 'common:profile.advancedOptions',
        },
        children: [
            {
                index: true,
                element: <ProfileAdvancedSettingsPageContent />,
            },
            {
                path: 'hotkeys',
                element: <ProfileHotkeysPageContent />,
                handle: {
                    crumbKey: 'common:hotkey.title',
                    title: 'common:hotkey.title',
                    description: 'common:hotkey.usagePrompt',
                },
            },

            {
                path: 'keybindings',
                element: <ProfileKeybindingsPageContent />,
                handle: {
                    crumbKey: 'common:keys.title',
                    title: 'common:keys.title',
                    description: 'common:keys.description',
                },
            },
            {
                path: 'dictation',
                element: <ProfileDictationPageContent />,
                handle: {
                    crumbKey: 'common:dictation.title',
                    title: 'common:dictation.title',
                    description: 'common:dictation.description',
                },
            },
            {
                path: 'suggested-prompts',
                element: <ProfileSuggestedPromptsPageContent />,
                handle: {
                    crumbKey: 'common:suggestedPrompts.title',
                    title: 'common:suggestedPrompts.title',
                    description: 'common:suggestedPrompts.description',
                },
            },
            {
                path: 'summary',
                element: <ProfileSummaryPageContent />,
                handle: {
                    crumbKey: 'common:profile.summary.title',
                    title: 'common:profile.summary.title',
                    description: 'common:profile.summary.description',
                },
            },
            {
                path: 'share',
                element: <ProfileSharePageContent />,
                handle: {
                    crumbKey: 'common:share',
                    title: 'common:profile.share.title',
                    description: 'common:profile.share.description',
                },
            },
        ],
    },
];
const getOrgRoutes = () => [
    {
        element: <OrgDetailPage />,
        index: true,
    },
    {
        path: 'billing/compare',
        handle: { crumbKey: 'common:billing.upgrade.compare' },
        element: <LicenseUpgradePage />,
    },
    {
        path: 'billing/redirect',
        children: getBillingRoutes(),
    },
    {
        path: 'trial/redirect',
        children: getBillingRoutes(),
    },
    {
        path: 'license',
        element: <OrgLicenseActivatedPage />,
        handle: { crumbKey: 'common:license.activate.title' },
    },
    {
        path: 'members',
        handle: { crumbKey: 'common:organization.member.titlePlural' },
        children: [
            {
                index: true,
                element: <OrgMembersPage />,
            },
            {
                path: 'invites',
                element: <OrgInvitesPage />,
                handle: { crumbKey: 'common:organization.invite.titlePlural' },
            },
        ],
    },
    {
        path: 'projects',
        handle: { crumbKey: 'common:organization.projects.title' },
        children: [
            {
                element: <ProjectsLandingPage />,
                index: true,
            },
            {
                path: 'new',
                element: <ProjectsNewPage />,
                handle: { crumbKey: 'common:organization.projects.create.title' },
            },
            {
                path: 'client/:clientId',
                element: <ProjectClientDetailPage />,
                handle: { crumbKey: 'common:organization.projects.clients.title' },
            },
            {
                path: 'device/:deviceId',
                handle: { crumbKey: 'common:organization.projects.deviceType.title' },
                children: [
                    {
                        index: true,
                        element: <ProjectDeviceTypeDetailPage />,
                    },
                    {
                        path: 'customControls',
                        element: <ProjectDeviceTypeCustomControlsPage />,
                        handle: { crumbKey: 'common:organization.projects.deviceType.customControls.title' },
                    },
                ],
            },
        ],
    },
    {
        path: 'metrics',
        handle: { crumbKey: 'common:organization.metrics.title' },
        children: [
            {
                element: <MetricLandingPage />,
                index: true,
            },
            {
                path: ':clientId',
                element: <ToBeCreated pageTitle="Org Metrics Client Detail" />,
                handle: { crumbKey: 'common:organization.project.title' },
            },
        ],
    },
    {
        path: 'documentation',
        handle: { crumbKey: 'common:resources.title' },
        children: [
            {
                element: <OrgDocumentationPage />,
                index: true,
            },
        ],
    },
    {
        path: 'settings',
        handle: { crumbKey: 'common:organization.settings.title' },
        children: [
            {
                element: <OrgSettingsPage />,
                index: true,
            },
        ],
    },
    {
        path: 'profiles',
        handle: { crumbKey: 'common:profile.title', title: 'common:profile.title' },
        children: [
            {
                element: <OrgProfilesPage />,
                index: true,
            },
            {
                path: 'new',
                handle: {
                    crumbKey: 'common:create',
                },
                element: <ProfileLayout />,
                children: getProfileRoutes(),
            },
            {
                path: 'edit/:profileId',
                handle: {
                    crumbKey: 'common:edit',
                },

                element: <ProfileLayout />,
                children: getProfileRoutes(),
            },
            {
                path: 'preview/:profileId',
                handle: {
                    crumbKey: 'common:profile.preview',
                    description: 'common:profile.previewDescription',
                },
                element: <ProfileLayout />,
                children: [
                    {
                        index: true,
                        element: <ProfilePreviewPageContent />,
                    },
                ],
            },
        ],
    },
];

const router = createHashRouter([
    {
        path: '/',
        handle: { crumbKey: 'common:dashboard.title' },
        children: [
            {
                path: 'login',
                element: (
                    <AdminHeaderOnlyLayout>
                        <LoginPage />
                    </AdminHeaderOnlyLayout>
                ),
            },
            {
                path: 'authcallback',
                element: (
                    <AdminHeaderOnlyLayout>
                        <AuthCallbackPage />
                    </AdminHeaderOnlyLayout>
                ),
            },
            {
                element: <ProtectedLayout />,
                children: [
                    {
                        index: true,
                        element: <DashboardPage />,
                    },
                    {
                        path: 'upgrade',
                        handle: { crumbKey: 'common:upgrade' },
                        element: <LicenseUpgradePage />,
                    },
                    {
                        path: 'my-account',
                        element: <AccountSettingsPage />,
                        handle: { crumbKey: 'common:account.settings' },
                    },
                    {
                        path: 'notifications',
                        element: <NotificationPage />,
                        handle: { crumbKey: 'common:notifications.title' },
                    },

                    {
                        path: 'org/:organizationId',
                        handle: { crumbKey: 'common:organization.title' },
                        children: [
                            ...getOrgRoutes(),
                            {
                                path: 'managed',
                                element: <OrgManagedOrgsPage />,
                                handle: { crumbKey: 'common:organization.managed.title' },
                            },
                            {
                                path: 'managed/:managedOrgId',
                                handle: { crumbKey: 'common:organization.managed.title' },
                                children: getOrgRoutes(),
                            },
                            {
                                path: 'licensekey',
                                element: <OrgLicenseKeyPage />,
                                handle: { crumbKey: 'common:license.key.title' },
                            },
                            {
                                path: 'purchase',
                                element: <OrgProductPage />,
                                handle: { crumbKey: 'common:license.key.product.title' },
                            },
                            {
                                path: 'product/:productId',
                                element: <OrgBillingRedirectPage />,
                                handle: { crumbKey: 'common:license.redirect.title' },
                            },
                        ],
                    },

                    {
                        path: 'developer',
                        element: <DeveloperToolsPage />,
                        handle: { crumbKey: 'common:developer.title' },
                    },
                    {
                        path: 'support',
                        element: <ToBeCreated pageTitle="Support" />,
                        handle: { crumbKey: 'common:support.title' },
                    },
                ],
            },
            {
                path: '*',
                element: (
                    <AdminHeaderOnlyLayout>
                        <NotFoundPage />
                    </AdminHeaderOnlyLayout>
                ),
                handle: { crumbKey: 'common:notFound' },
            },
        ],
    },
]);

export default function Layout() {
    const restructureUrl = () => {
        const { search, hash } = window.location;
        // If there are query parameters but they are before the hash
        // specifically addresses the base url overwriting that happens with auth callbacks
        if (search && !hash.includes('?')) {
            let newHash = hash || '#/';
            const params = new URLSearchParams(search);

            // Directly move to authcallback if code is present to minimize the number of redirects
            if (search.includes('code')) {
                newHash = '#/authcallback';
            }

            const newUrl = `${window.location.origin}${newHash}${search}`;
            window.history.replaceState(null, '', newUrl);
            window.location.reload();
        }
    };

    useEffect(() => {
        restructureUrl();
    }, []);
    return <RouterProvider router={router} />;
}
